import React, {useCallback, useMemo} from 'react';
import {
  DepartmentDataServiceDeleteFn,
  DepartmentDataServiceFormValues,
  DepartmentDataServiceUpdateFn
} from "./DepartmentDataServiceForm.types";
import {useForm} from "antd/es/form/Form";
import {FormInstance} from "antd/es/form/hooks/useForm";
import {ServiceCatalogEntry} from "../../../models/DepartmentData/ServiceCatalogEntry";
import {DepartmentDataService} from "../../../models/DepartmentData/DepartmentDataService";
import {SelectOption} from "../../common/forms/Interfaces";
import {DEPARTMENT_DATA_SERVICES} from "../../../constants/translates";
import {Button, Form, Input, Select, Tooltip} from "antd";
import {DeleteOutlined} from "@ant-design/icons";
import {formItemFactory} from "../../formItems/FormItemFactory";
import validations from "./validations";
import {OptionMap} from "../../../helpers/optionMap";

type DepartmentDataServiceFormProps = {
  formId: string;
  onUpdate: DepartmentDataServiceUpdateFn;
  onDelete: DepartmentDataServiceDeleteFn;
  serviceList: ServiceCatalogEntry[];
  selectedServiceIds: string[];
  savedArchivedService?: Pick<DepartmentDataService, 'service_id' | 'name'>;
  disabled: boolean;
  initialValues?: Partial<DepartmentDataServiceFormValues>;
};

const DepartmentDataServiceItem = formItemFactory<DepartmentDataServiceFormValues>(validations);

const validate = async (form: FormInstance<DepartmentDataServiceFormValues>): Promise<boolean> => {
  try {
    await form.validateFields(['service_id', 'amount']);
    return true;
  } catch (error) {
    return error['errorFields'].length === 0;
  }
};

const DepartmentDataServiceForm = ({ formId, onUpdate, onDelete, serviceList, selectedServiceIds, savedArchivedService, disabled, initialValues }: DepartmentDataServiceFormProps) => {
  const [form]: [FormInstance<DepartmentDataServiceFormValues>] = useForm<DepartmentDataServiceFormValues>();
  const defaultAmounts: Map<string, string> = useMemo(() => {
    return new Map<string, string>(
      serviceList.map((service: ServiceCatalogEntry) => (
        [service.id, service.default_value]
      ))
    );
  }, [serviceList]);

  const serviceOptions: SelectOption[] = useMemo(() => {
    const map: Record<string, string> = Object.fromEntries(
      serviceList.map((service: ServiceCatalogEntry): [string, string] => [service.id, service.name])
    );
    if (
      savedArchivedService
      && !serviceList.some(
        (service: ServiceCatalogEntry): boolean => service.id === savedArchivedService.service_id
      )
    ) {
      map[savedArchivedService.service_id] =
        `${savedArchivedService.name} (${DEPARTMENT_DATA_SERVICES.archived})`;
    }

    const optionsMap: OptionMap<string> = new OptionMap<string>(map);
    return optionsMap.getSelectOptions().map((option: SelectOption): SelectOption => {
      return {
        ...option,
        disabled: selectedServiceIds.includes(option.value.toString()),
      };
    });
  }, [savedArchivedService, selectedServiceIds, serviceList]);

  const handleValuesChange = useCallback(async (changedValues: Partial<DepartmentDataServiceFormValues>): Promise<void> => {
    if(changedValues.service_id) {
      const amount: string = defaultAmounts.get(changedValues.service_id.toString()) || '0';
      form.setFieldsValue({
        amount,
      });
      const valid: boolean = await validate(form);
      await onUpdate({ service_id: changedValues.service_id, formId, amount, valid });
    } else {
      const valid: boolean = await validate(form);
      await onUpdate({ formId, amount: changedValues.amount, valid });
    }
  }, [defaultAmounts, form, onUpdate]);

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
      className={'accent-container'}
    >
      <Form
        form={form}
        initialValues={initialValues}
        onValuesChange={handleValuesChange}
        style={{ flex: 1, minWidth: 0 }}
      >
        <DepartmentDataServiceItem name={'service_id'} label={DEPARTMENT_DATA_SERVICES.service}>
          <Select options={serviceOptions} placeholder={DEPARTMENT_DATA_SERVICES.selectService} disabled={disabled} />
        </DepartmentDataServiceItem>
        <DepartmentDataServiceItem name={'amount'} label={DEPARTMENT_DATA_SERVICES.amount}>
          <Input type={'number'} placeholder={DEPARTMENT_DATA_SERVICES.enterAmount} disabled={disabled} />
        </DepartmentDataServiceItem>
      </Form>
      <Tooltip title={DEPARTMENT_DATA_SERVICES.remove}>
        <Button
          type="link"
          danger
          aria-label={DEPARTMENT_DATA_SERVICES.remove}
          disabled={disabled}
          onClick={() => onDelete(formId)}
        >
          <DeleteOutlined style={{ fontSize: '18px' }} />
        </Button>
      </Tooltip>
    </div>
  );
};

export default DepartmentDataServiceForm;